/*
 * Who may read an article (2026-10-26, ENHANCEMENTS S46).
 *
 * An article is PUBLIC - everybody may read it once it is live, as every article
 * could before - or PRIVATE: read only by the signed-in readers in one of its
 * AUDIENCES, and by the knowledge base's administrators. This is not a second
 * publishing gate. Whether an article is out at all is still IsVisible,
 * PublishAtUtc and a current revision (publishing.ts); visibility says to whom.
 *
 * AN AUDIENCE IS CONFIGURATION, NOT DATA
 *   Each audience is a pair of application settings on the website:
 *
 *     VISIBILITY_AUDIENCE_NAME_<KEY>       the name an editor ticks and a reader sees
 *     VISIBILITY_AUDIENCE_OBJECTID_<KEY>   the Entra ID group whose members read it
 *
 *   A pair with a non-empty value in both is a configured audience; the KEY is the
 *   part the two setting names share, upper-cased. The database stores only keys
 *   (kb.ArticleAudience.AudienceKey), never group ids, and the browser never sees
 *   a group id at all - the server maps a reader's group claim to keys.
 *
 * The key's grammar is CK_ArticleAudience_AudienceKey's: A-Z, digits and
 * underscores, 1-100 characters. A setting whose key falls outside it is not an
 * audience the database could store, and the server says so at startup.
 */
/**
 * Tied to the ArticleVisibility union in types.ts by `satisfies`, the
 * arrangement SITE_THEMES uses, so the zod enum built from it accepts exactly
 * the union's members. 'public' first: it is the default.
 */
export const ARTICLE_VISIBILITIES = ['public', 'private'];
export const AUDIENCE_NAME_SETTING_PREFIX = 'VISIBILITY_AUDIENCE_NAME_';
export const AUDIENCE_OBJECT_ID_SETTING_PREFIX = 'VISIBILITY_AUDIENCE_OBJECTID_';
/** kb.ArticleAudience.AudienceKey is VARCHAR(100). */
export const AUDIENCE_KEY_MAX_LENGTH = 100;
/** CK_ArticleAudience_AudienceKey, as a pattern. */
export const AUDIENCE_KEY_PATTERN = /^[A-Z0-9_]{1,100}$/;
/**
 * How many audiences one article may name. Not a database rule - the table has
 * no ceiling - but a list longer than this is a mistake rather than a choice,
 * and a bound keeps the request small.
 */
export const MAX_ARTICLE_AUDIENCES = 50;
export function isValidAudienceKey(key) {
    return AUDIENCE_KEY_PATTERN.test(key);
}
/** The editor's two words for the two values. */
export function describeArticleVisibility(visibility) {
    return visibility === 'private' ? 'Private' : 'Public';
}
/**
 * What an article nobody has said anything about is: public, with no audiences.
 * Frozen through, list included, so no caller can change it for the next one.
 */
export const PUBLIC_READERS = Object.freeze({
    visibility: 'public',
    audienceKeys: Object.freeze([]),
});
/**
 * Who reads an article after an ingest request (2026-10-26, S46): what it had,
 * changed by what the request supplied. One rule for both operations - a create
 * passes PUBLIC_READERS as what it had, an update what is stored - so the two
 * cannot come to disagree about what a payload means.
 *
 *   - visibility: the supplied one, or the one the article has.
 *   - private: the supplied keys, each once and in key order, or - when none are
 *     supplied - the ones the article has. At least one, or nobody but the
 *     administrators could read it.
 *   - public: no keys. Supplying some is REFUSED, where the editor's contract
 *     drops them: the editor's checklist keeps its ticks while it is hidden, but
 *     a machine that sends audiences for a public article has said two things
 *     that disagree, and reading them as "public" is the direction that opens an
 *     article it meant to restrict.
 *
 * An empty list is no list, as everywhere else on the ingest surface: an
 * extracted public article carries `audienceKeys: []`, and echoing it back must
 * not be refused. Whether a key is CONFIGURED is the server's to check, before
 * this runs.
 */
export function resolveIngestReaders(current, supplied) {
    const visibility = supplied.visibility ?? current.visibility;
    const suppliedKeys = supplied.audienceKeys?.length ? [...new Set(supplied.audienceKeys)].sort() : undefined;
    if (visibility === 'public') {
        if (suppliedKeys) {
            return {
                refusal: 'audienceKeys restricts a private article, and this article would be public. Send ' +
                    '"visibility": "private" with them, or leave audienceKeys out.',
            };
        }
        return { readers: { visibility, audienceKeys: [] } };
    }
    const audienceKeys = suppliedKeys ?? [...current.audienceKeys];
    if (audienceKeys.length === 0) {
        return { refusal: 'A private article needs at least one audience: send its keys in audienceKeys.' };
    }
    return { readers: { visibility, audienceKeys } };
}
//# sourceMappingURL=articleVisibility.js.map